import React, { useContext, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import cx from 'classnames'

import { Avatar } from '@driftt/tide-core'
import { access } from '@driftt/traffic-control'

import ActivityContentToolTip from 'components/ActivityContentTooltip/index'
import Education from 'components/Education'
import { EDUCATION_STORE_NAMES } from 'components/Education/constants'
import OnlineIndicator from 'components/OnlineIndicator'
import TooltipHoverPortal from 'components/TooltipHoverPortal'
import config from 'config'
import { CurrentUserContext } from 'context/currentUserContext'
import usePathUtils from 'hooks/usePathUtils'
import { AccountActivity, ActivityGroupByEndUser } from 'modules/accountActivity/domain'
import { Account, ActivityEndUser, CompanyData } from 'modules/accountActivity/domain'
import { EngagementScore } from 'pages/AccountsPage'
import { accountPath, contactPath } from 'utils/paths'
import { timeSinceUpdated } from 'utils/timeStamp'
import { segmentTrack, TrackType } from 'utils/tracking'

import utilityStyles from '../../../../styles/utility.module.css'
import styles from './styles.module.css'

interface CardHeaderProps {
  insightId: number
  visitorList?: ActivityEndUser[]
  accountData?: Account
  accountActivity?: AccountActivity
  companyData?: CompanyData
  hasOnlineUser?: boolean
  timestampMs: number
  title?: React.ReactElement
  isFirstCard?: boolean
  contact?: ActivityGroupByEndUser
}

const CardHeader: React.FC<CardHeaderProps> = ({
  insightId,
  visitorList = [],
  accountData,
  companyData,
  accountActivity,
  hasOnlineUser = false,
  timestampMs,
  title,
  isFirstCard = false,
  contact
}) => {
  const currentUser = useContext(CurrentUserContext)
  const [tooltipVisible, setTooltipVisible] = useState(true)
  const { isAccountActivityPage } = usePathUtils()

  const numOtherContacts = visitorList.length - 1
  const accountDisplayName =
    accountData?.name || companyData?.name || companyData?.domain || 'Unnamed account'
  const firstKnownContact = visitorList.find(visitor => !!visitor?.email || !!visitor?.name)

  const ref = useRef<HTMLDivElement>(null)
  const refElement = useRef<HTMLDivElement>(null)

  const isWhitelisted = config.ACCOUNT_ACTIVITY_TIMELINE_WHITELIST?.includes(currentUser?.id)
  const hasTimeline = access.hasGates('account_activity_timeline') || isWhitelisted
  const hasSeenEduTooltip = !!localStorage.getItem(EDUCATION_STORE_NAMES.ACTIVITY_TIMELINE)
  const hasEducationTooltip =
    isAccountActivityPage && hasTimeline && isFirstCard && tooltipVisible && !hasSeenEduTooltip

  const createCompanyLinkWithName = (data: Account, companyData?: CompanyData) => (
    <Link
      className={styles.companyName}
      to={accountPath(data.accountId)}
      onClick={() => {
        segmentTrack(TrackType.INSIGHT_FEED_VIEW_ACCOUNT, {
          insightId: insightId,
          accountId: data.accountId
        })
      }}>
      {companyData ? (
        <ActivityContentToolTip data={companyData}>
          <div ref={refElement}>{accountDisplayName}</div>
        </ActivityContentToolTip>
      ) : (
        <div ref={refElement}>{accountDisplayName}</div>
      )}
    </Link>
  )

  const onCloseEducation = () => {
    localStorage.setItem(EDUCATION_STORE_NAMES.ACTIVITY_TIMELINE, 'true')
    setTooltipVisible(false)
  }

  return (
    <div className={styles.headerContainer} ref={ref} id="headerContainer">
      <div className={styles.avatarAndContact}>
        {accountData ? (
          <Link
            to={accountPath(accountData.accountId)}
            onClick={() => {
              segmentTrack(TrackType.INSIGHT_FEED_VIEW_ACCOUNT, {
                insightId: insightId,
                accountId: accountData?.accountId
              })
            }}>
            <Avatar className={styles.Avatar} type="company" company={companyData} />
          </Link>
        ) : (
          <Avatar className={styles.Avatar} type="company" company={companyData} />
        )}
        <div className={styles.headerText}>
          {title ?? (
            <div className={styles.firstContactAndCompany}>
              {visitorList.length > 0 && (
                <div className={cx(styles.contactAndNumOthers, utilityStyles.semiBoldWeight)}>
                  <Link
                    onClick={() =>
                      segmentTrack(TrackType.INSIGHT_FEED_VIEW_CONTACT, {
                        insightId: insightId,
                        accountId: accountData?.accountId,
                        contactId: firstKnownContact?.id || visitorList[0].id
                      })
                    }
                    to={contactPath(firstKnownContact?.id || visitorList[0].id)}>
                    <ActivityContentToolTip data={contact}>
                      <span className={styles.contact}>
                        {firstKnownContact?.name || firstKnownContact?.email || 'Site visitor'}
                      </span>
                    </ActivityContentToolTip>
                  </Link>
                  {numOtherContacts > 0 && (
                    <span>
                      {' + '}
                      {numOtherContacts} {numOtherContacts === 1 ? 'other' : 'others'}
                    </span>
                  )}
                </div>
              )}
              {accountData &&
                !hasEducationTooltip &&
                createCompanyLinkWithName(accountData, companyData)}
              {accountData && hasEducationTooltip && (
                <TooltipHoverPortal
                  placement={'bottom'}
                  isAlwaysOpen={true}
                  boundaryRef={refElement.current}
                  refElement={refElement.current}
                  tooltipBody={<Education type={'ACTIVITY_TIMELINE'} onClose={onCloseEducation} />}>
                  {createCompanyLinkWithName(accountData, companyData)}
                </TooltipHoverPortal>
              )}
            </div>
          )}
          <div className={styles.cardSubheader}>
            <span className={styles.cardSubheaderText}>{`${timeSinceUpdated(
              timestampMs
            )} ago`}</span>
            {hasOnlineUser && (
              <div className={styles.onlineBadge}>
                <OnlineIndicator online={true} className={styles.onlineBadgeDot} />
                <p>Online</p>
              </div>
            )}
          </div>
        </div>
      </div>
      {accountActivity && (
        <div className={styles.engagementScore}>
          <EngagementScore boundaryRef={ref.current} accountActivity={accountActivity} />
        </div>
      )}
    </div>
  )
}

export default CardHeader
